import {
  CODEC_CHOICES,
  DEFAULT_EXPORT_CONFIG,
  EXPORT_FPS,
  EXPORT_PRESETS,
  QUALITY_PRESETS,
  RESOLUTIONS,
  durationLabel,
  fileSizeLabel,
  matchingPreset,
  phaseLabel,
  planExport,
  presetById,
  snapshotConfig,
  type AspectRatio,
  type CodecChoice,
  type ExportConfig,
  type ExportPhase,
  type ExportRecipeChoice,
  type QualityPreset,
  type ResolutionName,
} from '../visual/index.js';

/**
 * The production export, as something a person can operate.
 *
 * Everything underneath it already worked before this existed: Phase 7A encoded
 * frames, 7B added the song's audio and a container, and a harness in the
 * console drove both. What was missing was the part where somebody who is not
 * holding the source code can choose a shape, press a button, and end up with a
 * file. That is all this is.
 *
 * ## Where it sits
 *
 * Folded away, in the same corner and the same idiom as the visual panel, and
 * hidden entirely during play. Export is something you do *to* a performance
 * once it is over, so the panel appears with the result and stays shut until
 * asked for. The brief has been explicit since Phase 3 that the play surface
 * must not fill with controls, and a video exporter is exactly the kind of thing
 * that would.
 *
 * ## What it decides
 *
 * As little as possible. Which numbers a preset means, what the output size
 * will be, what the file is called, which phase a progress report represents —
 * all of that is in `ExportPlan.ts`, which has no DOM in it and is tested
 * without a browser. This file is the wiring: elements, events, and the states
 * a running job can be in.
 */

const STYLE_ID = 'mandalizm-export-style';

const CSS = `
.mandalizm-export {
  /* Above the screen overlays — title 45, select 50, result 55 — because the
     export panel has to stay operable on the result screen, which is exactly
     where somebody reaches for it. Below that it renders and cannot be clicked. */
  /* Stacked by the dock rather than pinned to the corner; see the note in
     the visual panel's stylesheet. */
  position: relative; width: 100%;
  font: 11px/1.6 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; color: #bff4ee;
}
.mandalizm-export[hidden] { display: none; }
.mandalizm-export__toggle {
  appearance: none; cursor: pointer; font: inherit; letter-spacing: 0.18em;
  width: 100%; padding: 9px 12px; color: #ffe6bd; text-align: left;
  background: rgba(34, 20, 3, 0.9); border: 1px solid rgba(255, 190, 105, 0.34);
  border-radius: 6px;
}
.mandalizm-export__toggle:hover { background: rgba(72, 44, 6, 0.94); }
.mandalizm-export__body {
  margin-top: 6px; padding: 12px; box-sizing: border-box;
  background: rgba(0, 12, 18, 0.93); border: 1px solid rgba(255, 190, 105, 0.24);
  border-radius: 6px; max-height: 62vh; overflow-y: auto;
}
.mandalizm-export__body[hidden] { display: none; }
.mandalizm-export__group { margin-bottom: 11px; }
.mandalizm-export__label {
  font-size: 9px; letter-spacing: 0.24em; color: rgba(230, 190, 140, 0.7);
  display: block; margin-bottom: 5px;
}
.mandalizm-export__row { display: flex; gap: 5px; flex-wrap: wrap; }
.mandalizm-export__chip {
  appearance: none; cursor: pointer; font: inherit; font-size: 10px; letter-spacing: 0.1em;
  flex: 1 1 auto; padding: 6px 8px; color: #e8d4b4;
  background: rgba(26, 18, 6, 0.9); border: 1px solid rgba(255, 190, 105, 0.22);
  border-radius: 4px;
}
.mandalizm-export__chip[aria-pressed="true"] {
  color: #fff6e6; background: rgba(120, 74, 8, 0.95); border-color: rgba(255, 205, 130, 0.65);
}
.mandalizm-export__chip[disabled] { opacity: 0.34; cursor: not-allowed; }
.mandalizm-export__chip--go {
  color: #04231a; background: rgba(120, 245, 190, 0.9);
  border-color: rgba(160, 255, 215, 0.8); font-weight: 700;
}
.mandalizm-export__chip--go[disabled] { opacity: 0.34; }
.mandalizm-export__chip--stop { color: #ffd2d2; border-color: rgba(255, 140, 150, 0.5); }
.mandalizm-export__out {
  margin-top: 6px; font-size: 10px; color: #ffe6bd; font-variant-numeric: tabular-nums;
}
.mandalizm-export__hint { font-size: 10px; color: rgba(215, 185, 150, 0.6); margin-top: 3px; }
.mandalizm-export__bar {
  height: 5px; margin: 7px 0 5px; border-radius: 3px; overflow: hidden;
  background: rgba(255, 210, 150, 0.16);
}
.mandalizm-export__fill { height: 100%; width: 0%; background: rgba(120, 245, 190, 0.85); }
.mandalizm-export__status { font-size: 10px; color: #eafeff; font-variant-numeric: tabular-nums; }
.mandalizm-export__notice {
  margin-top: 7px; padding: 7px 8px; border-radius: 4px; font-size: 10px;
  background: rgba(90, 60, 0, 0.5); border: 1px solid rgba(255, 200, 110, 0.4); color: #ffe2ab;
}
.mandalizm-export__error {
  margin-top: 7px; padding: 7px 8px; border-radius: 4px; font-size: 10px;
  background: rgba(70, 8, 24, 0.6); border: 1px solid rgba(255, 140, 160, 0.5); color: #ffc2ce;
}
.mandalizm-export__done {
  margin-top: 7px; padding: 8px; border-radius: 4px; font-size: 10px;
  background: rgba(4, 40, 32, 0.7); border: 1px solid rgba(120, 245, 190, 0.4); color: #d6fff0;
}
.mandalizm-export__done b { color: #fff; }
.mandalizm-export__done dl { display: grid; grid-template-columns: auto 1fr; gap: 1px 10px; margin: 5px 0 0; }
.mandalizm-export__done dt { color: rgba(190, 235, 220, 0.65); }
.mandalizm-export__done dd { margin: 0; text-align: right; font-variant-numeric: tabular-nums; }
.mandalizm-export__caps {
  margin-top: 4px; font-size: 10px; color: rgba(190, 220, 225, 0.7);
  display: grid; grid-template-columns: auto 1fr; gap: 1px 10px;
}
.mandalizm-export__caps b { color: #eafeff; text-align: right; font-weight: 400; }
`;

export interface ExportCapabilities {
  readonly video: readonly string[];
  readonly audio: readonly string[];
  readonly containers: readonly string[];
  readonly maxTexture: number;
  readonly webCodecs: boolean;
}

export interface ExportDoneSummary {
  readonly fileName: string;
  readonly container: string;
  readonly width: number;
  readonly height: number;
  readonly fps: number;
  readonly durationSeconds: number;
  readonly bytes: number;
  readonly videoCodec: string;
  readonly audioCodec: string | null;
}

export interface ExportProgressView {
  readonly phase: ExportPhase;
  readonly renderedFrames: number;
  readonly totalFrames: number;
  readonly elapsedSeconds: number;
  readonly realtimeRatio: number;
}

export interface ExportPanelHandlers {
  readonly onStart: (config: ExportConfig) => void;
  readonly onCancel: () => void;
  readonly onDownload: () => void;
  readonly onStill: () => void;
  /** Fired when this panel's body opens or closes. See the note in `VisualPanel`. */
  readonly onOpenChanged?: (open: boolean) => void;
}

export class ExportPanel {
  readonly element: HTMLDivElement;

  private readonly body: HTMLDivElement;
  private readonly toggle: HTMLButtonElement;
  private readonly presetRow: HTMLDivElement;
  private readonly resolutionRow: HTMLDivElement;
  private readonly aspectRow: HTMLDivElement;
  private readonly fpsRow: HTMLDivElement;
  private readonly qualityRow: HTMLDivElement;
  private readonly codecRow: HTMLDivElement;
  private readonly audioRow: HTMLDivElement;
  private readonly recipeRow: HTMLDivElement;
  private readonly outLine: HTMLDivElement;
  private readonly hintLine: HTMLDivElement;
  private readonly startButton: HTMLButtonElement;
  private readonly cancelButton: HTMLButtonElement;
  private readonly stillButton: HTMLButtonElement;
  private readonly downloadButton: HTMLButtonElement;
  private readonly bar: HTMLDivElement;
  private readonly fill: HTMLDivElement;
  private readonly status: HTMLDivElement;
  private readonly notice: HTMLDivElement;
  private readonly error: HTMLDivElement;
  private readonly done: HTMLDivElement;
  private readonly caps: HTMLDivElement;

  private open = false;

  /** Whether the body is showing. The session uses it to lay the corner out. */
  get isOpen(): boolean {
    return this.open;
  }
  private config: ExportConfig = { ...DEFAULT_EXPORT_CONFIG };
  private running = false;
  private songDuration = 0;
  private unsupportedCodecs = new Set<string>();
  /** Why export is impossible here, if it is. See `setCapabilities`. */
  private unsupportedBrowser: string | null = null;
  private replayReady = false;

  constructor(
    private readonly handlers: ExportPanelHandlers,
    parent: HTMLElement = document.body,
  ) {
    injectStyle();
    this.element = document.createElement('div');
    this.element.className = 'mandalizm-export';
    this.element.hidden = true;

    this.toggle = document.createElement('button');
    this.toggle.type = 'button';
    this.toggle.className = 'mandalizm-export__toggle';
    this.toggle.textContent = '▸ EXPORT VIDEO';
    this.toggle.addEventListener('click', () => this.setOpen(!this.open));

    this.body = document.createElement('div');
    this.body.className = 'mandalizm-export__body';
    this.body.hidden = true;

    // Presets first: most exports are one of these four, and a user who wants
    // one should not have to set five controls to describe it.
    const presetGroup = group('PRESET');
    this.presetRow = row();
    for (const preset of EXPORT_PRESETS) {
      const chip = chipButton(preset.label, () => this.applyPreset(preset.id));
      chip.title = preset.note;
      chip.dataset.preset = preset.id;
      this.presetRow.appendChild(chip);
    }
    presetGroup.appendChild(this.presetRow);

    const resolutionGroup = group('RESOLUTION');
    this.resolutionRow = row();
    for (const name of RESOLUTIONS) {
      const chip = chipButton(name, () => this.patch({ resolution: name }));
      chip.dataset.resolution = name;
      this.resolutionRow.appendChild(chip);
    }
    resolutionGroup.appendChild(this.resolutionRow);

    const aspectGroup = group('ASPECT');
    this.aspectRow = row();
    for (const aspect of ['16:9', '9:16', '1:1'] as const) {
      const chip = chipButton(aspect, () => this.patch({ aspect }));
      chip.dataset.aspect = aspect;
      this.aspectRow.appendChild(chip);
    }
    aspectGroup.appendChild(this.aspectRow);

    // The output line lives with the size controls because that is the pairing
    // it exists to correct: `1080p` + `1:1` is a 1920×1920 frame, and the name
    // alone gives no hint of it.
    this.outLine = document.createElement('div');
    this.outLine.className = 'mandalizm-export__out';
    this.hintLine = document.createElement('div');
    this.hintLine.className = 'mandalizm-export__hint';
    aspectGroup.append(this.outLine, this.hintLine);

    const fpsGroup = group('FPS');
    this.fpsRow = row();
    for (const fps of EXPORT_FPS) {
      const chip = chipButton(String(fps), () => this.patch({ fps }));
      chip.dataset.fps = String(fps);
      this.fpsRow.appendChild(chip);
    }
    fpsGroup.appendChild(this.fpsRow);

    const qualityGroup = group('QUALITY');
    this.qualityRow = row();
    for (const quality of QUALITY_PRESETS) {
      const chip = chipButton(quality, () => this.patch({ quality }));
      chip.dataset.quality = quality;
      chip.title = QUALITY_NOTES[quality];
      this.qualityRow.appendChild(chip);
    }
    qualityGroup.appendChild(this.qualityRow);

    const codecGroup = group('CODEC');
    this.codecRow = row();
    for (const codec of CODEC_CHOICES) {
      const chip = chipButton(codec, () => this.patch({ codec }));
      chip.dataset.codec = codec;
      this.codecRow.appendChild(chip);
    }
    codecGroup.appendChild(this.codecRow);

    const audioGroup = group('AUDIO');
    this.audioRow = row();
    for (const [label, value] of [['ON', true], ['OFF', false]] as const) {
      const chip = chipButton(label, () => this.patch({ audio: value }));
      chip.dataset.audio = String(value);
      this.audioRow.appendChild(chip);
    }
    audioGroup.appendChild(this.audioRow);

    // The artwork, chosen separately from the file's shape. Same replay, a
    // different reading of it — which is the feature Phase 4 built and the one
    // thing here that changes what the video looks like rather than how big it
    // is.
    const recipeGroup = group('ARTWORK');
    this.recipeRow = row();
    for (const [label, value] of [
      ['ORIGINAL', 'original'],
      ['CURRENT', 'current'],
      ['RANDOMIZE', 'randomize'],
    ] as const) {
      const chip = chipButton(label, () => this.patch({ recipe: value }));
      chip.dataset.recipe = value;
      this.recipeRow.appendChild(chip);
    }
    recipeGroup.appendChild(this.recipeRow);

    const actionGroup = group('');
    const actionRow = row();
    this.startButton = chipButton('START EXPORT', () => this.start());
    this.startButton.classList.add('mandalizm-export__chip--go');
    this.cancelButton = chipButton('CANCEL', () => this.handlers.onCancel());
    this.cancelButton.classList.add('mandalizm-export__chip--stop');
    this.cancelButton.disabled = true;
    actionRow.append(this.startButton, this.cancelButton);

    const secondRow = row();
    this.stillButton = chipButton('EXPORT STILL', () => this.handlers.onStill());
    this.downloadButton = chipButton('DOWNLOAD VIDEO', () => this.handlers.onDownload());
    this.downloadButton.hidden = true;
    secondRow.append(this.stillButton, this.downloadButton);
    actionGroup.append(actionRow, secondRow);

    this.bar = document.createElement('div');
    this.bar.className = 'mandalizm-export__bar';
    this.bar.hidden = true;
    this.fill = document.createElement('div');
    this.fill.className = 'mandalizm-export__fill';
    this.bar.appendChild(this.fill);

    this.status = document.createElement('div');
    this.status.className = 'mandalizm-export__status';

    this.notice = document.createElement('div');
    this.notice.className = 'mandalizm-export__notice';
    this.notice.hidden = true;

    this.error = document.createElement('div');
    this.error.className = 'mandalizm-export__error';
    this.error.hidden = true;

    this.done = document.createElement('div');
    this.done.className = 'mandalizm-export__done';
    this.done.hidden = true;

    const capsGroup = group('THIS MACHINE');
    this.caps = document.createElement('div');
    this.caps.className = 'mandalizm-export__caps';
    capsGroup.appendChild(this.caps);

    this.body.append(
      presetGroup,
      resolutionGroup,
      aspectGroup,
      fpsGroup,
      qualityGroup,
      codecGroup,
      audioGroup,
      recipeGroup,
      actionGroup,
      this.bar,
      this.status,
      this.notice,
      this.error,
      this.done,
      capsGroup,
    );
    this.element.append(this.toggle, this.body);
    parent.appendChild(this.element);
    this.sync();
  }

  // -------------------------------------------------------------------------
  // State from outside
  // -------------------------------------------------------------------------

  setVisible(visible: boolean): void {
    this.element.hidden = !visible;
    if (!visible) this.setOpen(false);
  }

  setOpen(open: boolean): void {
    const opening = open && !this.open;
    const closing = !open && this.open;
    this.open = open;
    this.body.hidden = !open;
    this.toggle.textContent = `${open ? '▾' : '▸'} EXPORT VIDEO`;
    if (opening || closing) this.handlers.onOpenChanged?.(open);
  }

  /** How long the performance is, so frame counts can be shown. */
  setSong(durationSeconds: number, replayReady: boolean): void {
    this.songDuration = durationSeconds;
    this.replayReady = replayReady;
    this.sync();
  }

  /**
   * What this machine can encode.
   *
   * Codecs it cannot are disabled rather than hidden: an absent control makes a
   * user wonder whether they missed it, whereas a greyed one with a reason says
   * the question was asked and answered.
   */
  setCapabilities(caps: ExportCapabilities): void {
    this.unsupportedCodecs = new Set(
      CODEC_CHOICES.filter((codec) => codec !== 'AUTO' && !caps.video.includes(codec)),
    );
    this.caps.replaceChildren();
    const line = (label: string, value: string): void => {
      const dt = document.createElement('span');
      dt.textContent = label;
      const dd = document.createElement('b');
      dd.textContent = value;
      this.caps.append(dt, dd);
    };
    line('Video', caps.video.length > 0 ? caps.video.join(' / ') : 'none');
    line('Audio', caps.audio.length > 0 ? caps.audio.join(' / ') : 'none');
    line('Containers', caps.containers.join(' / '));
    line('Max frame', `${caps.maxTexture}px`);
    if (caps.webCodecs) {
      this.unsupportedBrowser = null;
      delete this.startButton.dataset.blocked;
    } else {
      // Said plainly rather than dressed up. A realtime screen recorder is not
      // the same thing as a deterministic offline render, and offering one as a
      // substitute would misrepresent what the file is.
      //
      // Held rather than just shown: this is a property of the browser, not the
      // outcome of a run, so it has to survive `clearOutcome` and the sync that
      // follows a replay arriving — which otherwise re-enabled START EXPORT on a
      // browser that cannot export, and took the explanation away with it.
      this.unsupportedBrowser =
        'Production offline export is unavailable in this browser — it needs WebCodecs. ' +
        'Still export and replay export still work.';
      this.startButton.dataset.blocked = '1';
    }
    this.sync();
  }

  setRunning(running: boolean): void {
    this.running = running;
    this.cancelButton.disabled = !running;
    this.bar.hidden = !running;
    this.sync();
  }

  showProgress(view: ExportProgressView): void {
    const pct = view.totalFrames > 0
      ? Math.min(100, Math.round((view.renderedFrames / view.totalFrames) * 100))
      : 0;
    this.fill.style.width = `${pct}%`;
    const speed = view.realtimeRatio > 0 ? ` · ${(1 / view.realtimeRatio).toFixed(2)}× realtime` : '';
    this.status.textContent =
      `${phaseLabel(view.phase)} — ${view.renderedFrames}/${view.totalFrames} (${pct}%) · ` +
      `${view.elapsedSeconds.toFixed(1)}s${speed}`;
  }

  showNotice(text: string | null): void {
    this.notice.textContent = text ?? '';
    this.notice.hidden = text === null;
  }

  showError(text: string | null): void {
    this.error.textContent = text ?? '';
    this.error.hidden = text === null;
    if (text !== null) {
      this.done.hidden = true;
      this.downloadButton.hidden = true;
    }
  }

  showCancelled(): void {
    this.status.textContent = phaseLabel('cancelled');
    this.done.hidden = true;
    // The point of the whole cancel path: nothing to download, because what
    // exists is not a file.
    this.downloadButton.hidden = true;
    this.fill.style.width = '0%';
  }

  showDone(summary: ExportDoneSummary): void {
    this.error.hidden = true;
    this.status.textContent = phaseLabel('done');
    this.fill.style.width = '100%';
    this.done.replaceChildren();
    const name = document.createElement('b');
    name.textContent = summary.fileName;
    const list = document.createElement('dl');
    const add = (label: string, value: string): void => {
      const dt = document.createElement('dt');
      dt.textContent = label;
      const dd = document.createElement('dd');
      dd.textContent = value;
      list.append(dt, dd);
    };
    add('Size', `${summary.width} × ${summary.height}`);
    add('Rate', `${summary.fps} fps`);
    add('Length', durationLabel(summary.durationSeconds));
    add('Codecs', `${summary.videoCodec}${summary.audioCodec ? ` + ${summary.audioCodec}` : ''}`);
    add('File', fileSizeLabel(summary.bytes));
    this.done.append(name, list);
    this.done.hidden = false;
    this.downloadButton.hidden = false;
  }

  /** Clears the previous run's outcome, so two exports do not blur together. */
  clearOutcome(): void {
    this.done.hidden = true;
    this.error.hidden = true;
    this.notice.hidden = true;
    this.downloadButton.hidden = true;
    this.status.textContent = '';
    this.fill.style.width = '0%';
    this.sync();
  }

  /** The configuration as it stands. Snapshot before handing it to a job. */
  get currentConfig(): ExportConfig {
    return snapshotConfig(this.config);
  }

  // -------------------------------------------------------------------------

  private start(): void {
    if (this.running) return;
    this.clearOutcome();
    // Snapshotted here, at the press, and never read from the panel again —
    // the controls stay live during an export and must not reach into it.
    this.handlers.onStart(snapshotConfig(this.config));
  }

  private applyPreset(id: string): void {
    const preset = presetById(id);
    if (preset === null) return;
    this.config = snapshotConfig(preset.config);
    this.sync();
  }

  private patch(change: Partial<ExportConfig>): void {
    this.config = snapshotConfig({ ...this.config, ...change });
    this.sync();
  }

  private sync(): void {
    const plan = planExport(this.config, this.songDuration);

    press(this.presetRow, 'preset', matchingPreset(this.config)?.id ?? '');
    press(this.resolutionRow, 'resolution', this.config.resolution);
    press(this.aspectRow, 'aspect', this.config.aspect);
    press(this.fpsRow, 'fps', String(this.config.fps));
    press(this.qualityRow, 'quality', this.config.quality);
    press(this.codecRow, 'codec', this.config.codec);
    press(this.audioRow, 'audio', String(this.config.audio));
    press(this.recipeRow, 'recipe', this.config.recipe);

    for (const chip of Array.from(this.codecRow.children) as HTMLButtonElement[]) {
      const codec = chip.dataset.codec ?? '';
      const unsupported = this.unsupportedCodecs.has(codec);
      chip.disabled = unsupported || this.running;
      chip.textContent = unsupported ? `${codec} · unsupported` : codec;
    }

    this.outLine.textContent = `Output: ${plan.sizeLabel}`;
    this.hintLine.textContent = plan.frames > 0
      ? `${this.config.fps} fps · ${plan.frames} frames · ${plan.durationSeconds.toFixed(2)}s`
      : 'no performance loaded';

    if (this.unsupportedBrowser !== null) {
      this.error.textContent = this.unsupportedBrowser;
      this.error.hidden = false;
    }
    this.startButton.disabled = this.running || !this.replayReady || this.startButton.dataset.blocked === '1';
    this.startButton.textContent = this.running ? 'EXPORTING…' : 'START EXPORT';
    this.stillButton.disabled = this.running || !this.replayReady;

    for (const group of [
      this.presetRow, this.resolutionRow, this.aspectRow, this.fpsRow,
      this.qualityRow, this.audioRow, this.recipeRow,
    ]) {
      for (const chip of Array.from(group.children) as HTMLButtonElement[]) {
        chip.disabled = this.running;
      }
    }
  }
}

const QUALITY_NOTES: Readonly<Record<QualityPreset, string>> = {
  LIVE: 'Fast preview',
  HIGH: 'Recommended export',
  ULTRA: 'Maximum visual quality',
};

function group(label: string): HTMLDivElement {
  const element = document.createElement('div');
  element.className = 'mandalizm-export__group';
  if (label.length > 0) {
    const span = document.createElement('span');
    span.className = 'mandalizm-export__label';
    span.textContent = label;
    element.appendChild(span);
  }
  return element;
}

function row(): HTMLDivElement {
  const element = document.createElement('div');
  element.className = 'mandalizm-export__row';
  return element;
}

function chipButton(label: string, onClick: () => void): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'mandalizm-export__chip';
  button.textContent = label;
  button.addEventListener('click', onClick);
  return button;
}

function press(container: HTMLElement, key: string, value: string): void {
  for (const child of Array.from(container.children) as HTMLElement[]) {
    child.setAttribute('aria-pressed', String(child.dataset[key] === value));
  }
}

function injectStyle(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

export type {
  AspectRatio,
  CodecChoice,
  ExportConfig,
  ExportRecipeChoice,
  QualityPreset,
  ResolutionName,
};
